import { LANGUAGE } from '@fitness-force/models';
import { environment } from './../../../../environments/environment';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  ErrorMsgComponent,
  NotFoundError,
  UnauthorizedError,
} from '@fitness-force/errors';
import { LoginData, LoginDataModel, LoginPayload } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthSevices } from 'libs/api/src/lib/auth.service';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { LangdropdownComponent } from '@fitness-force/common/language';
import {
  HelpsupportComponent,
} from '@fitness-force/authentication';
import { AUTH_STATE, COMPANY_INFO } from '../../../state/app.state';
import { Store } from '@ngrx/store';
import { Observable, Subscription, takeUntil, Subject } from 'rxjs';
import {
  LOGIN,
  LOGIN_SUCCESS,
  SELECT_LANGUAGE,
} from '../../../state/actions';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { AppInitService, ToastMessageService } from '@fitness-force/api';

import { PasswordModule } from 'primeng/password';
import { DividerModule } from 'primeng/divider';
import { IonicModule } from '@ionic/angular';
import { Platform } from '@angular/cdk/platform';
import { LoginProvider } from '@fitness-force/enum';
import { AvatarModule } from 'primeng/avatar';
import { CONSTANTS_DATA } from '@fitness-force/constants';
@Component({
  selector: 'fitness-force-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HttpClientModule,
    TranslateModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    DividerModule,
    PasswordModule,
    HelpsupportComponent,
    ErrorMsgComponent,
    LangdropdownComponent,
    IonicModule,
    AvatarModule
],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  loginForm: LoginData | any;
  errMsg: Error | any;
  companyDetails: any;
  passVisibility = false;
  passType = 'password';
  selectedCountry: any;
  showSupportPopup = false;
  isFormSubmitting = false;

  countries = [
    { name: 'Eng', code: 'gb' },
    { name: 'Ar', code: 'sa' }
  ];

  queryParams: Map<string, string | null>;

  public companyInfoDetail$!: Observable<any>;
  private messageHandler: (event: MessageEvent) => void;
  googleLoading = false;
  microsoftLoading = false;
  appleLoading = false;
  validCompanyFound = false;

  // Add subscription management
  private destroy$ = new Subject<void>();
  private authStateSubscription?: Subscription;

  constructor(
    private authService: AuthSevices,
    private fb: UntypedFormBuilder,
    public translate: TranslateService,
    private router: Router,
    public appInit: AppInitService,
    private activeRoute: ActivatedRoute,
    public store: Store,
    public changeDetectorRef: ChangeDetectorRef,
    public platform: Platform,
    private toastMessageService: ToastMessageService,
    public cd: ChangeDetectorRef
  ) {
    this.loginForm = this.fb.group({
      username: ['', {
        validators: [Validators.required, Validators.email],
        updateOn: 'change'
      }],
      password: ['', Validators.required],
      rememberdevice: [''],
    });

    this.messageHandler = this.handlePopupMessage.bind(this);
   
  }

  ngOnInit(): void {
    this.translate.setDefaultLang('en');
    this.translate.use('en');
    this.showPassword();
    this.store.dispatch({ type: 'FETCHED_COMPANY_DETAILS' });
    this.companyInfoDetail$ = this.store.select(COMPANY_INFO);
    
    // Use takeUntil for proper subscription management
    this.companyInfoDetail$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (comp) => {
        if (comp){
          this.companyDetails = comp;
        } 
        if (comp?.selectedLang?.LANG_CODE) {
          this.translate.setDefaultLang(comp?.selectedLang?.LANG_CODE);
          this.translate.use(comp?.selectedLang?.LANG_CODE);
        }
        if(comp?.compDetails?.uuid){
          this.validCompanyFound = true;
        }
        this.cd.detectChanges();
      },
      error: (err) => {
        console.error('Error loading company info:', err);
        this.validCompanyFound = false;
        this.cd.detectChanges();
      },
    });
    
     // Transform username to lowercase on value changes
     this.loginForm.get('username')?.valueChanges.pipe(
       takeUntil(this.destroy$)
     ).subscribe((value: string) => {
      if (value) {
        const lowercaseValue = value.toLowerCase();
        if (lowercaseValue !== value) {
          this.loginForm.patchValue({ username: lowercaseValue }, { emitEvent: false });
        }
      }
    });

    this.componentInit();
    window.addEventListener('message', this.messageHandler, false);
  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
    // Setup auth state subscription only once here
    this.setupAuthStateSubscription();
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
    
    window.removeEventListener('message', this.messageHandler, false);
  }

  componentInit() {
    // Don't call handleAuthState here to avoid duplicate subscriptions
    this.changeDetectorRef.detectChanges();
  }

  setupAuthStateSubscription() {
    // Unsubscribe from any existing subscription
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }

    this.authStateSubscription = this.store.select(AUTH_STATE).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (authState: any) => {
        if (authState && authState.loggedIn) {
          // User is logged in, no need to handle errors
        } else if (
          authState &&
          authState.error &&
          authState.status &&
          authState.status.toLowerCase() === 'error'
        ) {
          console.log('Handling login error:', authState.error);
          this.isFormSubmitting = false;
          try {
            const error = JSON.parse(authState.error);
            this.handleError(error);
          } catch (parseError) {
            console.error("Error parsing authState.error:", parseError, authState.error);
            this.handleError({ status: 500 });
          }
          this.cd.detectChanges();
        } else if (authState && authState.status && authState.status.toLowerCase() === 'in_progress') {
          console.log('Login in progress');
          // Keep the form submitting state
        } else {
          console.log('Auth state is in initial/other state');
          // Reset form submitting state if needed
          if (this.isFormSubmitting && !authState?.status) {
            this.isFormSubmitting = false;
            this.cd.detectChanges();
          }
        }
      },
      error: (error: any) => {
        this.isFormSubmitting = false;
        console.error('Error in auth state subscription:', error);
        this.cd.detectChanges();
      },
    });
  }

  showPassword() {
    this.passVisibility = !this.passVisibility;
    if (this.passVisibility) {
      this.passType = 'text';
    } else {
      this.passType = 'password';
    }
  }

  selectedLang(e: LANGUAGE) {
    localStorage.setItem('DEFAULT_LANGUAGE', JSON.stringify(e));
    this.store.dispatch(SELECT_LANGUAGE({ lang: e }));
  }

  closeSupportDialog(close: boolean) {
    this.showSupportPopup = false;
  }

  validateUserLogin(userAuthData: UntypedFormGroup): void {
    if(this.loginForm.valid && this.validCompanyFound){
      const userInfo: LoginDataModel = {
        email_id: userAuthData.value.username,
        password: userAuthData.value.password,
        company_uuid: this.companyDetails.compDetails.uuid,
      };
      this.isFormSubmitting = true;
      
      // Clear any previous error messages before attempting login
      this.clearAuthError();
      
      this.store.dispatch(LOGIN({ loginInput: userInfo }));
    }
    else{
      Object.keys(this.loginForm.controls).forEach(key => {
        this.loginForm.get(key)?.markAsTouched();
      });
      this.cd.detectChanges();
    }
  }

  private clearAuthError(): void {
    // This will help reset the error state for new login attempts
    // The auth state should be properly managed by the reducer
    this.errMsg = null;
  }

  handleError(error: any) {
    this.isFormSubmitting = false;
    this.cd.detectChanges();
    if(error.status == 500){
      this.translate
        .get('Auth.login.MSG.SOMETHING_WENT_WRONG')
        .subscribe((translation) => {
          this.toastMessageService.showErrorMessage(translation);
        });
        return;
    }
    if (error?.error.errorMessage) {
      this.translate
        .get('Auth.login.MSG.WRONG_USERNAME_PASS')
        .subscribe((translation) => {
          this.toastMessageService.showErrorMessage(translation);
        });
      return;
    }
    if (error?.originalError?.error) {
      if (error?.originalError?.error.errorCode == 'notfound') {
        this.translate
          .get('Auth.login.MSG.USER_NOT_FOUND')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
          this.cd.detectChanges();
          return;
      }
      if (error?.originalError?.error.errorCode == 'wrong') {
        this.translate
          .get('Auth.login.MSG.WRONG_USERNAME_PASS')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
          this.cd.detectChanges();
          return;
      }
    } else {
      if (error instanceof NotFoundError) {
        this.errMsg = error.originalError.message;
        this.translate
          .get('Auth.login.MSG.USER_NOT_FOUND')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
          this.cd.detectChanges();
          return;
      } else if (error instanceof UnauthorizedError) {
        this.translate
          .get('Auth.login.MSG.WRONG_USERNAME_PASS')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
          this.cd.detectChanges();
          return;
      } else {
        this.translate
          .get('Auth.login.MSG.SOMETHING_WENT_WRONG')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
        });
          this.cd.detectChanges();
          return;
      }
    }
    this.cd.detectChanges();
  }

  handleSocialError(error: any) {
    if (error.originalError && error.originalError.status == 404) {
         this.translate
          .get('Auth.login.MSG.USER_NOT_FOUND')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
    } else if (error.originalError && error.originalError.status == 500) {
      this.translate
          .get('Auth.login.MSG.INTERNAL_SERVER_ERROR')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
    } else {
      this.translate
          .get('Auth.login.MSG.SOMETHING_WENT_WRONG')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
    }
    this.cd.detectChanges();
  }

  hasTranslation(key: string): boolean {
    const translation = this.translate.instant(key);
    return translation !== key && translation !== '';
  }

  get Username(): UntypedFormControl {
    return this.loginForm.get('username');
  }

  get Password(): UntypedFormControl {
    return this.loginForm.get('password');
  }

  public openSocialLoginPopup(provider: 'google' | 'microsoft' | 'apple') {
    if (provider === 'google') {
      this.googleLoading = true;
    } else if (provider === 'microsoft') {
      this.microsoftLoading = true;
    } else if (provider === 'apple') {
      this.appleLoading = true;
    }
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const state = {
      company_uuid: this.companyDetails?.compDetails?.uuid,
      returnUrl: '',
      callbackUrl: environment.CALLBACK_URL,
      provider: provider,
      isCallback: 'false',
      origin: window.location.origin,
      authId:
        provider.toLowerCase() === 'google'
          ? environment.GOOGLE_AUTH_ID
          : provider.toLowerCase() === 'microsoft'
          ? environment.MICROSOFT_CLIENT_ID
          : provider.toLowerCase() === 'apple'
          ? environment.APPLE_ID
          : '',
    };
    const authId =
      provider.toLowerCase() === 'google'
        ? environment.GOOGLE_AUTH_ID
        : provider.toLowerCase() === 'microsoft'
        ? environment.MICROSOFT_CLIENT_ID
        : provider.toLowerCase() === 'apple'
        ? environment.APPLE_ID
        : '';
    const callbackUrl = environment.CALLBACK_URL;

    // Use the static HTML file instead of the Angular route
    var popup = window.open(
      `${
        environment.LOGIN_DOMAIN
      }/auth/login/social-login.html?provider=${provider}&state=${encodeURIComponent(
        JSON.stringify(state)
      )}&authId=${authId}&callbackUrl=${encodeURIComponent(callbackUrl)}`,
      `${provider}Login`,
      `width=${width},height=${height},left=${left},top=${top}`
    )!;

    // Monitor popup status
    const popupTimer = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(popupTimer);
        this.handlePopupClose(provider);
      }
    }, 1000);
    // Cleanup after timeout (optional)
    setTimeout(() => {
      clearInterval(popupTimer);
      if (popup && !popup.closed) {
        popup.close();
        this.handlePopupClose(provider);
      }
    }, 300000); // 5 minutes timeout
  }
  catch(error: any) {
    console.error('Error opening popup:', error);
  }

  handlePopupClose(provider: string) {
    this.googleLoading = false;
    this.microsoftLoading = false;
    this.appleLoading = false;
    this.changeDetectorRef.detectChanges();
  }

  private handlePopupMessage(event: MessageEvent) {
    if (event && event.data && event.data.token && event.origin !== environment.LOGIN_DOMAIN) {
      console.log('Invalid origin:', event.origin);
      return;
    }
    const { token, provider, error, state } = event.data;
    if (error) {
      this.handleSocialError(error);
      this.googleLoading = false;
      this.microsoftLoading = false;
      this.appleLoading = false;
      return;
    }

    if (token && provider && this.companyDetails) {
      this.isFormSubmitting = true;
      const payload: LoginPayload = {
        provider: this.getLoginProvider(provider),
        idToken: token,
        company_uuid: this.companyDetails.compDetails.uuid,
      };

      this.authService.socialLogin(payload).subscribe({
        next: (response) => {
          localStorage.setItem('token', response.token);
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token);
          }
          localStorage.setItem('auth_response', JSON.stringify(response));
          const user = this.authService.decodedJwtToken(response.token);
          this.store.dispatch(
            LOGIN_SUCCESS({ user, token: response.token, redirect: false, auth_response: response })
          );
          this.isFormSubmitting = false;
          this.googleLoading = false;
          this.microsoftLoading = false;
          this.appleLoading = false;
          this.socialLoginSuccess();
        },
        error: (error) => {
          console.error('Social login error:', error);
          this.handleSocialError(error);
          this.isFormSubmitting = false;
          this.googleLoading = false;
          this.microsoftLoading = false;
          this.appleLoading = false;
          this.changeDetectorRef.detectChanges();
        },
      });
    }
  }

  getLoginProvider(provider: string): LoginProvider {
    return provider.toLowerCase() === 'google'
      ? LoginProvider.Google
      : provider.toLowerCase() === 'microsoft'
      ? LoginProvider.Microsoft
      : provider.toLowerCase() === 'apple'
      ? LoginProvider.Apple
      : LoginProvider.FF;
  }

  socialLoginSuccess() {
    const returnUrl = this.activeRoute.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigateByUrl(
        CONSTANTS_DATA.DEFAULT.LOGIN_SUCCESS_REDIRECTION
      );
    }
    this.changeDetectorRef.detectChanges();
  }
}
